import type { RcAgentData } from './types';

/**
 * Recall agent demo — Alamo Heights Dental, last 30 days, **amendment 24**.
 *
 * Every figure here is an input that something else divides or adds. The two
 * anchors are the Dashboard's own 30-day Recall numbers: **131 booked from 1,140
 * patients contacted**. Everything that claims to break those down adds back up
 * to them, and `derive.ts` is where the adding happens.
 *
 * What is *not* here is as deliberate as what is: no cost per booking (we don't
 * track spend), no booking-rate trend line (the hero states the rate once), no
 * *benefits expiring* recall (there isn't one), no per-recall table (the hero's
 * segments are the per-recall view, and `/recall` is the full list).
 */
export const RECALL_AGENT: RcAgentData = {
  practiceName: 'Alamo Heights Dental',
  titleLead: 'Recall',
  titleAccent: 'agent',
  subtitle: 'Text, email, AI calls and human calls · Alamo Heights Dental (San Antonio, TX)',
  hero: {
    contacted: 1140,
    caption: '30 days',
    // 58 + 22 + 21 + 14 + 16 = 131. The headline is this sum, not a sixth number.
    segments: [
      { label: 'Hygiene — 6 month', value: 58 },
      { label: 'Annual exam', value: 22 },
      { label: 'Lapsed — 18 months and over', value: 21 },
      { label: 'Perio maintenance — 3 month', value: 14 },
      {
        // Grouped, but **named** on hover — *Two others* said 16 without saying
        // which two, and there were three of them (§2).
        label: 'Three others',
        value: 16,
        tooltip: 'Oral cancer screening 9 · Fluoride — under 14 3 · Whitening — summer 4',
      },
    ],
    productionScheduled: 24890,
  },
  // Three cards. Booking rate and Recalls running were cut: the hero's per-100
  // line and the header's eyebrow already say both (§3).
  sideCards: [
    {
      key: 'sent',
      label: 'Sent',
      // 780 + 690 + 150 + 31 — the channel rows add to this.
      value: '1,651',
      delta: '▲8.2%',
      caption: 'texts, emails and calls',
    },
    {
      key: 'reached',
      label: 'Patients reached',
      value: '1,042',
      delta: '▲6.1%',
      caption: 'got a message or answered a call',
    },
    {
      key: 'opt-out',
      label: 'Opt-out rate',
      value: '0.9%',
      caption: 'of everyone contacted',
    },
  ],
  // Thirteen weeks, bars only. The last four add to 131, so the chart and the
  // hero are describing the same month (§4).
  weeks: [
    { label: 'Jul 1', booked: 26 },
    { label: 'Jul 8', booked: 28 },
    { label: 'Jul 15', booked: 25 },
    { label: 'Jul 22', booked: 29 },
    { label: 'Jul 29', booked: 27 },
    { label: 'Aug 5', booked: 30 },
    { label: 'Aug 12', booked: 28 },
    { label: 'Aug 19', booked: 31 },
    { label: 'Aug 26', booked: 29 },
    { label: 'Sep 2', booked: 32 },
    { label: 'Sep 9', booked: 34 },
    { label: 'Sep 16', booked: 33 },
    { label: 'Sep 23', booked: 32 },
  ],
  // Bookings by the LAST message before they booked, so each booking is counted
  // once: 58 + 31 + 29 + 13 = 131. Before, the four rows added to 268 — every
  // channel was claiming the same bookings.
  channels: [
    { label: 'Text', sent: 780, booked: 58 },
    { label: 'Email', sent: 690, booked: 31 },
    { label: 'AI call', sent: 150, booked: 29 },
    { label: 'Human call', sent: 31, booked: 13 },
  ],
  // Only kinds we actually produce, and each one names its recall (§5.3).
  activity: [
    {
      id: 'e1',
      tone: 'outcome',
      text: 'Sarah Mitchell booked a cleaning for 2 Oct',
      recall: 'Hygiene — 6 month',
      at: '2 min ago',
    },
    {
      id: 'e2',
      tone: 'batch',
      text: '62 patients left Hygiene — 6 month — they booked another way',
      at: '9 min ago',
    },
    {
      id: 'e3',
      tone: 'agent',
      text: 'AI call to Linda Ramirez — voicemail left',
      recall: 'Hygiene — 6 month',
      at: '14 min ago',
    },
    {
      id: 'e4',
      tone: 'held',
      text: '14 emails held — they’ve had 3 messages this week',
      recall: 'Lapsed — 18 months and over',
      at: '21 min ago',
    },
    {
      id: 'e5',
      tone: 'escalated',
      text: 'Call Kevin Wright — added to Calls',
      recall: 'Lapsed — 18 months and over',
      at: '38 min ago',
    },
    {
      id: 'e6',
      tone: 'agent',
      text: 'ELVA corrected a date in an email to Tom Reyes before it went out',
      recall: 'Annual exam',
      at: '52 min ago',
    },
    {
      id: 'e7',
      tone: 'held',
      text: 'Maria Gonzalez asked to stop these messages',
      recall: 'Hygiene — 6 month',
      at: '1h ago',
    },
    {
      id: 'e8',
      tone: 'outcome',
      text: 'James Harrison booked perio maintenance for 18 Oct',
      recall: 'Perio maintenance — 3 month',
      at: '1h ago',
    },
    { id: 'e9', tone: 'batch', text: 'Birthday — sent to 3 patients', at: '2h ago' },
  ],
  historyDays: 214,
};
